import { isAxiosError } from 'axios'
import i18next from 'i18next'
import toast from 'react-hot-toast'
import type { MarqueeImportResult } from '@/sdk'

/** Kept visible longer than a standard toast: the create flow redirects right
 * after the upload, so the operator often only glances back at the result. */
const IMPORT_TOAST_DURATION_MS = 10_000

/**
 * Success summary for an invitee CSV upload: how big the roster now is and how
 * it resolved, plus what a re-import did to its hand reconciliation.
 *
 * `imported` is counted over the **whole roster after the import**, revived
 * hand-added rows included — not over the file. So it is stated as "attendees on
 * the roster", and it is the same population `resolved` and `unresolved` split,
 * which is what makes the three read as one arithmetic. Calling it a file count
 * is the trap: the numbers stop adding up the moment a hand-added row revives.
 * The file's own row count is `imported - manual_carried + rows_dropped`, and
 * nothing here has a reason to show it.
 *
 * The carry lines render only when the backend reports a non-zero counter, so a
 * first import (nothing to carry) stays the single summary line it was. A pin or
 * mark reported as dropped is information, not an error: it means the attendee is
 * simply not in the new invitee list, which is a legitimate thing for a corrected
 * file to say.
 *
 * `rows_dropped` is the dedupe count, reported for the same reason: the merge is
 * correct, the operator just has to be told it happened (OPSSTD-5140).
 */
export const showImportSuccessToast = (result: MarqueeImportResult) => {
	const {
		imported = 0,
		resolved = 0,
		unresolved = 0,
		rows_dropped = 0,
		pins_carried = 0,
		pins_dropped = 0,
		marks_carried = 0,
		marks_dropped = 0,
		manual_carried = 0,
		manual_superseded = 0
	} = result

	const carryLines = [
		pins_carried > 0 && {
			key: 'pins_carried',
			text: i18next.t('marquee:import_pins_carried', {
				count: pins_carried
			})
		},
		pins_dropped > 0 && {
			key: 'pins_dropped',
			text: i18next.t('marquee:import_pins_dropped', {
				count: pins_dropped
			})
		},
		marks_carried > 0 && {
			key: 'marks_carried',
			text: i18next.t('marquee:import_marks_carried', {
				count: marks_carried
			})
		},
		marks_dropped > 0 && {
			key: 'marks_dropped',
			text: i18next.t('marquee:import_marks_dropped', {
				count: marks_dropped
			})
		},
		manual_carried > 0 && {
			key: 'manual_carried',
			text: i18next.t('marquee:import_manual_carried', {
				count: manual_carried
			})
		},
		manual_superseded > 0 && {
			key: 'manual_superseded',
			text: i18next.t('marquee:import_manual_superseded', {
				count: manual_superseded
			})
		},
		rows_dropped > 0 && {
			key: 'rows_dropped',
			text: i18next.t('marquee:import_rows_dropped', {
				count: rows_dropped
			})
		}
	].filter((line) => line !== false)

	return toast.success(
		<div className='flex flex-col gap-0.5'>
			<span className='font-medium'>
				{i18next.t('marquee:import_success')}
			</span>
			<span className='text-neutral-500 text-xs'>
				{i18next.t('marquee:import_success_summary', {
					imported,
					resolved,
					unresolved
				})}
			</span>
			{carryLines.map((line) => (
				<span className='text-neutral-500 text-xs' key={line.key}>
					{line.text}
				</span>
			))}
		</div>,
		{ duration: IMPORT_TOAST_DURATION_MS }
	)
}

/**
 * The import endpoint rejects an unreadable CSV with `code: "invalid-upload"`
 * and a raw English `message`. Map that code to a friendly toast; everything
 * else falls back to the generic error message.
 */
export const handleImportError = (error: unknown) => {
	if (
		isAxiosError(error) &&
		error.response?.data?.code === 'invalid-upload'
	) {
		return toast.error(i18next.t('marquee:error_invalid_upload'), {
			duration: IMPORT_TOAST_DURATION_MS
		})
	}

	if (isAxiosError(error)) {
		const message =
			error.response?.data.message && error.status !== 500
				? i18next.t(`errors:${error.response.data.message}`)
				: i18next.t('errors:default')
		return toast.error(message, {
			duration: IMPORT_TOAST_DURATION_MS
		})
	}

	return toast.error(i18next.t('errors:default'), {
		duration: IMPORT_TOAST_DURATION_MS
	})
}
